import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PRODUCT_STATUS_CLASS,
  PRODUCT_STATUS_LABEL,
  useAllProductsCatalog,
  useCreateProduct,
  useDeleteProduct,
  useUpdateProduct,
  type ProductCatalogInput,
  type ProductCatalogItem,
  type ProductStatus,
} from "@/hooks/useProductsCatalog";

interface ProductsCatalogManagerProps {
  embedded?: boolean;
}

const STATUS_OPTIONS = Object.keys(PRODUCT_STATUS_LABEL) as ProductStatus[];

const fieldClass = "h-10 rounded-[10px] bg-muted border-border text-[13.5px]";
const areaClass = "rounded-[10px] bg-muted border-border text-[13.5px] min-h-[84px]";
const labelClass = "text-xs font-semibold text-muted-foreground";

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const linesToArray = (text: string) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

interface FormState {
  name: string;
  slug: string;
  status: ProductStatus;
  description: string;
  pricing: string;
  stack: string;
  deadline: string;
  image_url: string;
  display_order: string;
  features: string;
  included: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  slug: "",
  status: "disponivel",
  description: "",
  pricing: "",
  stack: "",
  deadline: "",
  image_url: "",
  display_order: "0",
  features: "",
  included: "",
};

function toForm(p: ProductCatalogItem): FormState {
  return {
    name: p.name,
    slug: p.slug,
    status: p.status,
    description: p.description ?? "",
    pricing: p.pricing ?? "",
    stack: p.stack ?? "",
    deadline: p.deadline ?? "",
    image_url: p.image_url ?? "",
    display_order: String(p.display_order ?? 0),
    features: (p.features ?? []).join("\n"),
    included: (p.included ?? []).join("\n"),
  };
}

function toInput(f: FormState): ProductCatalogInput {
  const nullable = (v: string) => (v.trim() ? v.trim() : null);
  return {
    name: f.name.trim(),
    slug: f.slug.trim(),
    status: f.status,
    description: nullable(f.description),
    pricing: nullable(f.pricing),
    stack: nullable(f.stack),
    deadline: nullable(f.deadline),
    image_url: nullable(f.image_url),
    display_order: Number.parseInt(f.display_order, 10) || 0,
    features: linesToArray(f.features),
    included: linesToArray(f.included),
  };
}

export function ProductsCatalogManager({ embedded = false }: ProductsCatalogManagerProps) {
  const { data: products = [], isLoading } = useAllProductsCatalog();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProductCatalogItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [toDelete, setToDelete] = useState<ProductCatalogItem | null>(null);

  useEffect(() => {
    if (!editing && !slugTouched) {
      setForm((f) => ({ ...f, slug: slugify(f.name) }));
    }
  }, [form.name, editing, slugTouched]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, display_order: String(products.length + 1) });
    setSlugTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (p: ProductCatalogItem) => {
    setEditing(p);
    setForm(toForm(p));
    setSlugTouched(true);
    setDialogOpen(true);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const saving = createProduct.isPending || updateProduct.isPending;
  const canSave = form.name.trim().length > 0 && form.slug.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    const input = toInput(form);
    if (editing) {
      await updateProduct.mutateAsync({ id: editing.id, ...input });
    } else {
      await createProduct.mutateAsync(input);
    }
    setDialogOpen(false);
  };

  return (
    <div className="flex flex-col gap-[22px]">
      {!embedded && (
        <div>
          <h1 className="mb-1.5 font-display text-[27px] font-bold tracking-[-0.03em]">Catálogo</h1>
          <p className="text-[13.5px] text-muted-foreground">
            Produtos prontos exibidos em /produtos
          </p>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-border px-[22px] pb-3.5 pt-[18px]">
          <h2 className="text-[15px] font-bold">Catálogo de produtos</h2>
          <Button className="h-8 rounded-[9px] px-3 text-xs font-semibold" onClick={openCreate}>
            Novo produto
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : products.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhum produto cadastrado.
          </p>
        ) : (
          products.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-4 border-b border-border px-[22px] py-[15px] last:border-b-0 hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-[13.5px] font-semibold">{p.name}</span>
                  <span
                    className={`inline-flex h-[21px] items-center rounded-[7px] px-2 text-[10.5px] font-bold ${PRODUCT_STATUS_CLASS[p.status]}`}
                  >
                    {PRODUCT_STATUS_LABEL[p.status]}
                  </span>
                </div>
                <div className="mt-0.5 truncate font-mono text-[11.5px] text-muted-foreground">
                  /produtos/{p.slug}
                  {p.stack ? ` · ${p.stack}` : ""}
                </div>
              </div>
              <span className="hidden max-w-[220px] shrink-0 truncate text-[12.5px] text-muted-foreground md:block">
                {p.pricing ?? "—"}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-[31px] w-[31px] text-bad hover:text-bad"
                onClick={() => setToDelete(p)}
                aria-label="Excluir produto"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                className="h-[31px] rounded-lg px-[13px] text-xs font-semibold"
                onClick={() => openEdit(p)}
              >
                Editar
              </Button>
            </div>
          ))
        )}
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-[640px] overflow-y-auto rounded-[18px] p-[26px]">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              {editing ? "Editar produto" : "Novo produto"}
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Produto pronto exibido no catálogo público em /produtos.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="product-nome" className={labelClass}>
                Nome *
              </Label>
              <Input
                id="product-nome"
                className={fieldClass}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-slug" className={labelClass}>
                Slug *
              </Label>
              <Input
                id="product-slug"
                className={`${fieldClass} font-mono`}
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug", slugify(e.target.value));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-status" className={labelClass}>
                Status
              </Label>
              <Select value={form.status} onValueChange={(v) => set("status", v as ProductStatus)}>
                <SelectTrigger id="product-status" className={fieldClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {PRODUCT_STATUS_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-ordem" className={labelClass}>
                Ordem
              </Label>
              <Input
                id="product-ordem"
                type="number"
                className={fieldClass}
                value={form.display_order}
                onChange={(e) => set("display_order", e.target.value)}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="product-descricao" className={labelClass}>
                Descrição
              </Label>
              <Textarea
                id="product-descricao"
                className={areaClass}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-pricing" className={labelClass}>
                Pricing
              </Label>
              <Input
                id="product-pricing"
                className={fieldClass}
                placeholder="A partir de R$ 3.500 · implantação única"
                value={form.pricing}
                onChange={(e) => set("pricing", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-stack" className={labelClass}>
                Stack
              </Label>
              <Input
                id="product-stack"
                className={`${fieldClass} font-mono`}
                placeholder="React · Supabase · n8n"
                value={form.stack}
                onChange={(e) => set("stack", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-prazo-de-implantacao" className={labelClass}>
                Prazo de implantação
              </Label>
              <Input
                id="product-prazo-de-implantacao"
                className={fieldClass}
                placeholder="7 a 14 dias, incluindo migração e treinamento."
                value={form.deadline}
                onChange={(e) => set("deadline", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-url-da-imagem" className={labelClass}>
                URL da imagem
              </Label>
              <Input
                id="product-url-da-imagem"
                className={fieldClass}
                placeholder="https://…"
                value={form.image_url}
                onChange={(e) => set("image_url", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-features-uma-por-linha" className={labelClass}>
                Features (uma por linha)
              </Label>
              <Textarea
                id="product-features-uma-por-linha"
                className={`${areaClass} min-h-[120px]`}
                value={form.features}
                onChange={(e) => set("features", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-incluso-uma-por-linha" className={labelClass}>
                Incluso (uma por linha)
              </Label>
              <Textarea
                id="product-incluso-uma-por-linha"
                className={`${areaClass} min-h-[120px]`}
                value={form.included}
                onChange={(e) => set("included", e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="mt-3 gap-[9px]">
            <Button
              variant="outline"
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={handleSave}
              disabled={!canSave || saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!toDelete}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px] p-[26px]">
          <div className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-bad-soft text-bad">
            <Trash2 className="h-5 w-5" />
          </div>
          <AlertDialogHeader className="mt-1 space-y-2 text-left">
            <AlertDialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              Excluir produto &quot;{toDelete?.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              O produto some do catálogo público imediatamente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-3 gap-[9px]">
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) deleteProduct.mutate(toDelete.id);
                setToDelete(null);
              }}
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
